import Util from '../analytics/util';
import Constants from '../analytics/constants';
import Core from '../core/Core';
import Options from '../analytics/plugin/options';
import RequestBuilder from '../analytics/plugin/requestbuilder';
import DeprecatedOptions from '../analytics/plugin/optionModules/deprecatedOptions';
import SessionsRequestHandler from './comm/SessionsRequestHandler';
import { POST } from '../analytics/constants/requestMethod';
import Log from '../common/log';
import { SessionsTag } from '../common/Constants';
import ListenerBus from './utils/ListenerBus';
import RegisteredPropertiesStore from './utils/RegisteredPropertiesStore';
import MetricsDiffer from './utils/MetricsDiffer';
import BeatScheduler from './utils/BeatScheduler';
import PluginLogger from './utils/PluginLogger';
import ParamsAssembler from './utils/ParamsAssembler';
import RequestDispatcher from './utils/RequestDispatcher';
export default class Session {
    // ------------------------------------------------------------------------------------------
    // CONSTRUCTOR / DESTRUCTOR
    // ------------------------------------------------------------------------------------------
    /**
     * Constructs the Sessions class
     */
    constructor(accountCode) {
        this.options = new Options();
        this.core = Core.getInstance();
        this.coreStorage = this.core.getCoreStorage();
        this.requestHandler = new SessionsRequestHandler(this.options);
        this.accountCode = accountCode;
        this.requestBuilder = new RequestBuilder();
        // Initialize refactored modules
        this.listenerBus = new ListenerBus();
        this.registeredPropertiesStore = new RegisteredPropertiesStore(this.coreStorage);
        this.metricsDiffer = new MetricsDiffer();
        this.beatScheduler = new BeatScheduler(this._sendBeat.bind(this), Session.BEAT_INTERVAL);
        this.paramsAssembler = new ParamsAssembler(() => this.registeredPropertiesStore.get());
        this.requestDispatcher = new RequestDispatcher(this.core, this.options, this.requestBuilder, this.requestHandler, this // Pass session context for RequestBuilder
        );
        this.pluginLogger = new PluginLogger(this.core, this.options, this.requestBuilder, this.requestHandler.queueRequest.bind(this.requestHandler), this.isMethodPostEnabled.bind(this));
        // Connect handler listeners to our listener bus
        this.requestHandler.onRequestQueued.push(this.onRequestQueued.bind(this));
        this.requestHandler.onWillSendRequest.push(this.onWillSendRequest.bind(this));
        this.requestHandler.onRequestSent.push(this.onRequestSent.bind(this));
        this._lastNavigation = { page: '', route: '' };
    }
    /**
     * Destroy session object
     */
    destroy() {
        this.beatScheduler.stop();
        this.requestHandler.destroy();
        // Clear listener arrays to prevent memory leaks
        this.listenerBus.clear();
    }
    /**
     * Starts a new session. If a session exists, stops it and starts a new one.
     *
     * @param options - Object of key:value options (optional).
     * @param dimensions - Object of key:value params to add to the request (optional).
     * @param onSuccess - Optional callback function to be executed if the session start is successful.
     * @param onFail - Optional callback function to be executed if the session start fails.
     * @param forceStart - Optional parameter to force sending the /start NQS event
     */
    start(options, dimensions = {}, onSuccess, onFail, forceStart = false) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot start session since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        if (options) {
            this.setOptions(options);
        }
        this.registeredPropertiesStore.refresh();
        this.pluginLogger.logFireSessionStartEvent(dimensions);
        // Prepare params and send request
        const params = this.paramsAssembler.assembleForStart(dimensions);
        this.requestDispatcher.dispatch(Constants.Service.SESSION_START, params, forceStart, onSuccess, onFail);
    }
    /**
     * Stop session
     * @param params
     * @param onSuccess
     * @param onFail
     * @param forceStop
     * @returns
     */
    stop(params = {}, onSuccess, onFail, forceStop = false) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot stop session since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        this.pluginLogger.logFireSessionStopEvent(params);
        // Prepare params and send request
        const preparedParams = this.paramsAssembler.assembleForStop(params);
        this.requestDispatcher.dispatch(Constants.Service.SESSION_STOP, preparedParams, forceStop, onSuccess, onFail);
        this.metricsDiffer.reset();
    }
    /**
     * Sends session event
     * @param eventName
     * @param dimensions
     * @param values
     * @param topLevelDimensions
     * @param hasEndDatetime
     * @param onSuccess
     * @param onFail
     * @returns
     */
    sendEvent(eventName = '', dimensions = {}, values = {}, topLevelDimensions = {}, hasEndDatetime = false, onSuccess, onFail) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot send session event since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        this.pluginLogger.logFireEventListener(eventName);
        const params = this.paramsAssembler.assembleForEvent(eventName, dimensions, values, topLevelDimensions, {
            hasEndDatetime
        });
        this.requestDispatcher.dispatch(Constants.Service.EVENT, params, false, onSuccess, onFail);
    }
    /**
     * Sends session custom event end/stop
     * @param eventName
     * @param topLevelDimensions
     * @param onSuccess
     * @param onFail
     * @returns
     */
    sendEventEnd(eventName = '', topLevelDimensions = {}, onSuccess, onFail) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot send session event since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        this.pluginLogger.logFireEventEndListener(eventName);
        const params = this.paramsAssembler.assembleForEventEnd(eventName, topLevelDimensions);
        this.requestDispatcher.dispatch(Constants.Service.EVENT_END, params, false, onSuccess, onFail);
    }
    /**
     * Emits session start request.
     *
     * @param dimensions - Object of key:value params to add to the request.
     * @param onSuccess - Optional callback function to be executed if the request is successful.
     * @param onFail - Optional callback function to be executed if the request fails.
     */
    fireNav(dimensions = {}, onSuccess, onFail) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot send session navigation since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        this.pluginLogger.logFireNavListener(dimensions);
        const params = this.paramsAssembler.assembleForNav(dimensions, false);
        if (this._checkDifferentNavigation(params)) {
            this.requestDispatcher.dispatch(Constants.Service.NAV, params, false, onSuccess, onFail);
            this._lastNavigation.page = params.page;
            this._lastNavigation.route = params.route;
        }
        else {
            Log.warn(SessionsTag, 'Same navigation detected and ignored for Page and Route');
            onFail === null || onFail === void 0 ? void 0 : onFail();
        }
    }
    /**
     * Emits session error
     * @param code
     * @param msg
     * @param errorType
     * @param metadata
     * @param duration
     * @param dimensions
     * @param values
     * @param onSuccess
     * @param onFail
     * @returns
     */
    fireError(code, msg, errorType = '', metadata = '', duration = 0, dimensions = {}, values = {}, onSuccess, onFail) {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot send session error since session is using legacy');
            onFail === null || onFail === void 0 ? void 0 : onFail();
            return;
        }
        this.pluginLogger.logFireSessionErrorEvent(code, msg, errorType, metadata, duration, dimensions, values);
        // Assemble base params with dimensions and values
        let params = this.paramsAssembler.assembleForEvent('', dimensions, values, {}, { hasEndDatetime: false });
        params = this.paramsAssembler.assembleForError(params, { code, msg, errorType, metadata, duration });
        this.requestDispatcher.dispatch(Constants.Service.SESSION_ERROR, params, false, onSuccess, onFail);
    }
    /**
     * Sends beat request
     *
     * @param diffTime - Time since the last ping
     *
     * @private
     */
    _sendBeat(diffTime) {
        const params = {
            diffTime: diffTime
        };
        this.requestDispatcher.dispatch(Constants.Service.BEAT, params, false);
    }
    // ------------------------------------------------------------------------------------------
    // SESSION EVENT HELPERS
    // ------------------------------------------------------------------------------------------
    /**
     * Check if change page/route (to avoid duplicated navigations)
     * @param params
     * @returns {boolean}
     * @private
     */
    _checkDifferentNavigation(params) {
        params = params || {};
        const sameRoute = this._lastNavigation && params.route === this._lastNavigation.route;
        const samePage = this._lastNavigation && params.page === this._lastNavigation.page;
        return !sameRoute || !samePage;
    }
    // ------------------------------------------------------------------------------------------
    // SUSPEND / RESUME
    // ------------------------------------------------------------------------------------------
    /**
     * Suspend session
     */
    suspend() {
        Log.notice(SessionsTag, 'Suspending session');
        // Send beat immediately if session is active before suspending it
        if (this.isActive()) {
            const diffTime = this.beatScheduler.suspend();
            this._sendBeat(diffTime);
        }
        else {
            this.beatScheduler.stop();
        }
    }
    /**
     * Resume session
     */
    resume() {
        Log.notice(SessionsTag, 'Resuming session');
        if (this.isActive()) {
            const expireTime = this.core.getFastDataService().getSessionExpireTime() * 1000;
            const { shouldBeat, diffTime } = this.beatScheduler.resume(expireTime);
            // Send beat immediately if session has not expired
            if (shouldBeat) {
                this._sendBeat(diffTime);
            }
            // Start beat timer when session is active
            this.beatScheduler.start();
        }
    }
    // ------------------------------------------------------------------------------------------
    // OPTION MANAGEMENT
    // ------------------------------------------------------------------------------------------
    /**
     * Sets Analytics options. See {@link Options.setOptions}.
     *
     * @internal
     * @param options
     */
    setOptions(options) {
        try {
            if (options && Object.keys(options).length > 0) {
                // Apply deprecated options mapping for backward compatibility
                const deprecatedOptions = new DeprecatedOptions();
                const mappedOptions = {};
                for (const key in options) {
                    if (deprecatedOptions.exists(key)) {
                        const newKey = deprecatedOptions.getNewName(key);
                        mappedOptions[newKey] = options[key];
                    }
                    else {
                        mappedOptions[key] = options[key];
                    }
                }
                if (this.options && Object.keys(this.options).length > 0) {
                    Object.assign(this.options, mappedOptions);
                }
                else {
                    this.options = mappedOptions;
                }
                if (this.requestHandler && mappedOptions.maxWaitingRequests !== undefined) {
                    this.requestHandler.setMaxWaitingRequests(mappedOptions.maxWaitingRequests);
                }
            }
        }
        catch (e) {
            /* fail silently */
        }
    }
    /**
     * Gets Analytics options. See {@link Options}.
     */
    getOptions() {
        return this.options;
    }
    // ------------------------------------------------------------------------------------------
    // HELPERS
    // ------------------------------------------------------------------------------------------
    /**
     * Checks whether the session is using legacy
     * @returns
     */
    isUsingLegacy() {
        return this.core.isUsingLegacy();
    }
    /**
     * Get account code
     * @returns
     */
    getAccountCode() {
        return this.accountCode;
    }
    /**
     * Session is active if there is a start request in the queue or the session is already started
     * @returns
     */
    isActive() {
        return this.requestHandler.isActive();
    }
    /**
     * Checks whether session has expired or not
     * @returns
     */
    expiredSession() {
        return this.requestHandler.hasExpired();
    }
    /**
     * Whether a previous session is persisted in storage and visible to this
     * fresh plugin instance (i.e. ExpirationManager loaded `firstRequestTime`
     * from localStorage). Allows AppAnalytics.newSession() to distinguish a
     * truly first-ever start from a relaunch within `sessionExpire` that
     * inherited a stale `sessionRoot` from the previous instance.
     *
     * @returns true if a persisted session token is detected
     */
    hasPersistedSession() {
        if (this.isUsingLegacy())
            return false;
        return this.requestHandler.isSessionStarted();
    }
    /**
     * Flush the request queue
     * @param timeoutMs - Timeout in milliseconds (default: 30000)
     * @returns Promise that resolves when the request queue is flushed
     */
    flush(timeoutMs) {
        return this.requestHandler.flush(timeoutMs);
    }
    /**
     * Destroys the active session synchronously: drops queued events, wipes session
     * storage and refreshes the session token. Does NOT trigger a SESSION_EXPIRE event
     * nor an automatic recovery — call start() afterwards to begin a new session.
     */
    destroySession() {
        if (this.core.isUsingLegacy()) {
            Log.warn(SessionsTag, 'Cannot destroy session since session is using legacy');
            return;
        }
        this.beatScheduler.stop();
        this.metricsDiffer.reset();
        this.requestHandler.destroySession();
        this._lastNavigation = { page: '', route: '' };
    }
    // ------------------------------------------------------------------------------------------
    // REGISTER / UNREGISTER
    // ------------------------------------------------------------------------------------------
    /**
     * Register properties sent by the User, to send in all the events
     *
     * @param dimensions - Object of key:value dimensions.
     * @param values - Object of key:value values.
     */
    register(dimensions = {}, values = {}) {
        this.registeredPropertiesStore.register(dimensions, values);
    }
    /**
     * Calls register if registeredProperties is empty
     *
     * @param dimensions - Object of key:value dimensions.
     * @param values - Object of key:value values.
     */
    registerOnce(dimensions, values) {
        this.registeredPropertiesStore.registerOnce(dimensions, values);
    }
    /**
     * Unregister all properties registered with register()
     */
    unregister() {
        this.registeredPropertiesStore.unregister();
    }
    // ------------------------------------------------------------------------------------------
    // PRIVATE LISTENERS
    // ------------------------------------------------------------------------------------------
    /**
     * On queue request listener
     * @param serviceName
     * @param params
     */
    onRequestQueued(request) {
        // Notify Product Analytics + App Analytics
        this.listenerBus.fireListeners('queued', request);
    }
    /**
     * On will send request listener
     * @param serviceName
     * @param params
     */
    onWillSendRequest(request) {
        switch (request.getService()) {
            case Constants.Service.SESSION_STOP:
                this.beatScheduler.stop();
                break;
        }
        // Notify Product Analytics + App Analytics
        this.listenerBus.fireListeners('willSend', request);
    }
    /**
     * On sent request listener
     * @param serviceName
     * @param params
     */
    onRequestSent(request) {
        switch (request.getService()) {
            case Constants.Service.SESSION_STOP:
                break;
            default:
                /*
                Enable beat timer in case it is not:
                  In non SPA, the session/start event may not be fired. This is why we need to start the beat
                  timer when any request is triggered. The session/stop stops the timer when needed.
                */
                if (!this.beatScheduler.isRunning && !this.beatScheduler.isSuspended()) {
                    this.beatScheduler.start();
                }
                break;
        }
        // Notify Product Analytics + App Analytics
        this.listenerBus.fireListeners('sent', request);
    }
    // ------------------------------------------------------------------------------------------
    // PUBLIC LISTENERS
    // ------------------------------------------------------------------------------------------
    /**
     * Add listener
     * @param eventName
     * @param callback
     */
    addListenerRequestQueued(listener) {
        if (listener) {
            this.listenerBus.addListener('queued', listener);
        }
    }
    /**
     * Add listener
     * @param eventName
     * @param callback
     */
    addListenerRequestWillSend(listener) {
        if (listener) {
            this.listenerBus.addListener('willSend', listener);
        }
    }
    /**
     * Add listener
     * @param eventName
     * @param callback
     */
    addListenerRequestSent(listener) {
        if (listener) {
            this.listenerBus.addListener('sent', listener);
        }
    }
    // ------------------------------------------------------------------------------------------
    // GETTERS
    // ------------------------------------------------------------------------------------------
    /**
     * Returns a json with the metrics to be reported in beats when changed
     */
    getSessionMetrics() {
        const currentSessionMetrics = Util.getMetricsFrom(this.options['session.metrics']) || {};
        return this.metricsDiffer.getChangedMetrics(currentSessionMetrics);
    }
    /**
     * Is player event logs enabled (to report QA Tools additional info)
     *
     * @internal
     * @returns {boolean}
     */
    isPlayerLogsEnabled() {
        return this.pluginLogger.isPlayerLogsEnabled();
    }
    /**
     * Check if is set protection on newSession method, to avoid stop the active session on newSession call
     *
     * @internal
     * @returns {boolean}
     */
    isSimpleNewSession() {
        if (this.options) {
            return Util.parseBoolean(this.options['simpleNewSession']);
        }
        return false;
    }
    /**
     * Is plugin logs enabled (to report QA Tools additional info)
     *
     * @internal
     * @returns {boolean}
     */
    isPluginLogsEnabled() {
        return this.pluginLogger.isPluginLogsEnabled();
    }
    /**
     * Is post method enabled
     * @returns {boolean}
     */
    isMethodPostEnabled() {
        return this.options.method && this.options.method.toUpperCase() === POST;
    }
    getSessionRoot() {
        return this.core.getFastDataSessionToken();
    }
    getSessionHost() {
        return this.core.getFastDataSessionHost();
    }
    // ------------------------------------------------------------------------------------------
    // LOGS (delegated to PluginLogger)
    // ------------------------------------------------------------------------------------------
    /**
     * Send plugin logs (public for compatibility)
     *
     * @param willSendLog
     * @param service
     * @param params
     */
    _sendPluginLogs(willSendLog, service, params) {
        this.pluginLogger.sendPluginLogs(willSendLog, service, params);
    }
    _logFireSessionStartEvent(dimensions) {
        this.pluginLogger.logFireSessionStartEvent(dimensions);
    }
    _logFireSessionErrorEvent(code, msg, errorType = '', metadata = '', duration = 0, dimensions = {}, values = {}) {
        this.pluginLogger.logFireSessionErrorEvent(code, msg, errorType, metadata, duration, dimensions, values);
    }
    _logFireSessionStopEvent(eventParams) {
        this.pluginLogger.logFireSessionStopEvent(eventParams);
    }
    _logFireEventListener(eventName) {
        this.pluginLogger.logFireEventListener(eventName);
    }
    _logFireEventEndListener(eventName) {
        this.pluginLogger.logFireEventEndListener(eventName);
    }
    _logFireNavListener(dimensions) {
        this.pluginLogger.logFireNavListener(dimensions);
    }
}
Session.BEAT_INTERVAL = 30000;
